/**
 * The supplier's EDI 997 acknowledgement of a claim batch, as the 844 page
 * shows it. An acknowledgement only says the supplier could read the batch —
 * it is shown next to the submission status, never in place of it.
 */

import { msg, T, useGT, useMessages } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EDI844AcknowledgementStatusSchema,
  type EDI844AcknowledgementStatus,
  type EDI844SubmissionActivity,
} from '@erp/rpc';
import { parseAcknowledgementFilter } from './acknowledgementFilter';

const LABELS = {
  awaiting: msg('Awaiting acknowledgement', {
    context: 'ship-and-debit 844 EDI 997 acknowledgement status',
  }),
  acknowledged: msg('Acknowledged', {
    context: 'ship-and-debit 844 EDI 997 acknowledgement status',
  }),
  acknowledged_with_errors: msg('Acknowledged with errors', {
    context: 'ship-and-debit 844 EDI 997 acknowledgement status',
  }),
  // Shown under "Supplier ack": the 997 rejected the file, not the claim.
  rejected: msg('Rejected', {
    context:
      "ship-and-debit 844 EDI 997 acknowledgement status: the supplier's EDI system rejected the file",
  }),
} satisfies Record<EDI844AcknowledgementStatus, string>;

const VARIANTS: Record<
  EDI844AcknowledgementStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  awaiting: 'outline',
  acknowledged: 'default',
  acknowledged_with_errors: 'secondary',
  rejected: 'destructive',
};

export function AcknowledgementStatusBadge({
  status,
}: {
  status: EDI844AcknowledgementStatus | null | undefined;
}) {
  const m = useMessages();
  if (!status) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant={VARIANTS[status]} className="whitespace-nowrap">
      {m(LABELS[status])}
    </Badge>
  );
}

/** The detail dialog's row; nothing for a claim with no acknowledgement. */
export function SubmissionAcknowledgementRow({
  status,
}: {
  status: EDI844AcknowledgementStatus | null | undefined;
}) {
  if (!status) return null;
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">
        <T context="ship-and-debit 844 detail label for the supplier's EDI 997 acknowledgement">
          Supplier acknowledgement
        </T>
      </span>
      <AcknowledgementStatusBadge status={status} />
    </div>
  );
}

export function AcknowledgementFilterSelect({
  value,
  onChange,
}: {
  value: EDI844AcknowledgementStatus | 'all';
  onChange: (value: EDI844AcknowledgementStatus | undefined) => void;
}) {
  const gt = useGT();
  const m = useMessages();
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        const parsed = parseAcknowledgementFilter(v);
        onChange(parsed === 'all' ? undefined : parsed);
      }}
    >
      <SelectTrigger className="w-[190px] !h-7 text-sm text-muted-foreground cursor-pointer hover:bg-accent hover:text-accent-foreground">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">
          {gt('All acknowledgements', {
            context: 'ship-and-debit 844 filter option for any 997 status',
          })}
        </SelectItem>
        {EDI844AcknowledgementStatusSchema.options.map((status) => (
          <SelectItem key={status} value={status}>
            {m(LABELS[status])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The supplier's 997 error notes, on an acknowledgement timeline entry. */
export function AcknowledgementActivityErrors({
  activity,
}: {
  activity: Pick<EDI844SubmissionActivity, 'activityType' | 'metadata'>;
}) {
  if (activity.activityType !== 'acknowledgement_received') return null;
  const errors = activity.metadata?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const notes = errors.filter((e): e is string => typeof e === 'string');
  return (
    <div className="mt-1 rounded-md border border-destructive/30 bg-destructive/5 p-2">
      <p className="text-xs font-medium text-destructive">
        <T context="ship-and-debit 844 timeline: heading over a supplier's 997 error notes">
          Errors the supplier reported
        </T>
      </p>
      <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
        {notes.map((note) => (
          <li key={note} className="font-mono">
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
}
